/*
 * KOJI CHAT - PAGINA PRINCIPALE (elenco stanze, cambio stanza, pannello laterale)
 *
 * Usato da: sections/koji-chat-hub.liquid  (template page.chat)
 * La conversazione vera e propria la gestisce assets/koji-chat.js (stesso motore delle chat in home e nelle collezioni):
 * qui si decide solo QUALE stanza mostrare. Stanze = collezione Firestore "rooms" (una riga per collezione), ordinate
 * dalla più commentata alla meno commentata. Una stanza compare solo se la sua collezione esiste e ha prodotti.
 */

const FIRESTORE_URL = 'https://www.gstatic.com/firebasejs/10.12.2/firebase-firestore.js';
const MAX_ROOMS = 60;
const INFO_BATCH = 30;

function esc(v) {
  return String(v == null ? '' : v).replace(/[&<>"'`]/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' }[c];
  });
}

function titleCase(handle) {
  return String(handle || '').replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
}

function norm(v) {
  return String(v || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
}

function cdn(src, w) {
  if (!src || src.indexOf('https://cdn.shopify.com/') !== 0) return '';
  return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
}

/* immagini caricate dal negozio (icona, sfondo): accettate se arrivano dalla CDN di Shopify o dal sito stesso */
function asset(src, w) {
  if (!src) return '';
  try {
    const u = new URL(src, window.location.origin);
    if (u.protocol !== 'https:') return '';
    if (u.hostname !== 'cdn.shopify.com' && u.hostname !== window.location.hostname) return '';
    if (w && !/\.svg$/i.test(u.pathname)) u.searchParams.set('width', w);
    return u.href;
  } catch (e) { return ''; }
}

const adultCache = new Map();
/* universi 16+: la pagina della collezione dichiara se è ecchi/hentai; quelli non compaiono mai nella chat */
function isAdult(handle) {
  if (!adultCache.has(handle)) {
    adultCache.set(handle, fetch('/collections/' + encodeURIComponent(handle), { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) { return /"roomMeta"\s*:\s*\{[^}]*"adult"\s*:\s*true/.test(html); })
      .catch(function () { return false; }));
  }
  return adultCache.get(handle);
}

function ago(ts) {
  if (!ts) return '';
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return 'proprio ora';
  if (m < 60) return m + ' min fa';
  const h = Math.floor(m / 60);
  if (h < 24) return h + (h === 1 ? ' ora fa' : ' ore fa');
  const d = Math.floor(h / 24);
  if (d < 7) return d + (d === 1 ? ' giorno fa' : ' giorni fa');
  return new Date(ts).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

function money(p) {
  const n = parseFloat(p);
  if (isNaN(n)) return '';
  try { return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }); } catch (e) { return '€' + n.toFixed(2); }
}

const infoCache = new Map();
/* una stanza è valida se la collezione esiste e ha almeno un prodotto; da qui arrivano anche foto e prodotti del pannello laterale.
   La foto è quella della collezione; se manca si usa quella del primo prodotto. */
function getInfo(handle) {
  if (!infoCache.has(handle)) {
    const h = encodeURIComponent(handle);
    const opts = { headers: { Accept: 'application/json' } };
    const prodP = fetch('/collections/' + h + '/products.json?limit=4', opts).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    const colP = fetch('/collections/' + h + '.json', opts).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    infoCache.set(handle, Promise.all([prodP, colP]).then(function (res) {
      const j = res[0];
      const c = res[1] && res[1].collection;
      if (!j || !j.products || !j.products.length) return { ok: false };
      const products = j.products.slice(0, 4).map(function (p) {
        return { title: p.title, handle: p.handle, img: p.images && p.images[0] ? p.images[0].src : '', price: p.variants && p.variants[0] ? p.variants[0].price : '' };
      });
      const colImg = c && c.image && c.image.src ? c.image.src : '';
      return { ok: true, img: colImg || products[0].img, products: products };
    }).catch(function () { return { ok: false }; }));
  }
  return infoCache.get(handle);
}

function init(hub) {
  const cfgEl = hub.querySelector('script[data-kh-config]');
  if (!cfgEl) return;
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { console.error('[koji-chat-hub] configurazione non valida', e); return; }
  const T = cfg.strings || {};
  const home = String(cfg.homeRoom || 'taverna-generale');
  const chatRoot = hub.querySelector('[data-koji-chat]');
  const listEl = hub.querySelector('[data-kh="list"]');
  const searchEl = hub.querySelector('[data-kh="search"]');
  const sideEl = hub.querySelector('[data-kh="side"]');
  const mobile = window.matchMedia ? window.matchMedia('(max-width: 768px)') : { matches: false };

  const resolved = new Map();
  const MEDALS = ['🥇', '🥈', '🥉'];
  const EMPTY_META = 'Ancora nessun messaggio: scrivi per primo!';
  let hits = [];
  let searchTok = 0;
  let searchTimer = null;
  let rooms = [];
  let roomsLoaded = false;
  let roomsError = false;
  let current = null;
  let kc = null;
  let introDone = false;
  let rendering = false;

  /* pulsante "indietro" (solo mobile) dentro l'intestazione della chat */
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'kh-back';
  back.setAttribute('aria-label', 'Torna alle stanze');
  back.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>';
  const headMain = chatRoot ? chatRoot.querySelector('.kc-head-main') : null;
  if (headMain) headMain.insertBefore(back, headMain.firstChild);
  back.addEventListener('click', function () {
    if (history.state && history.state.kh) history.back();
    else setView('list');
    focusAfterView('list');
  });

  listEl.setAttribute('role', 'group');
  function homeIconSrc() { return asset(cfg.homeIcon, 96); }
  const CHAT_SVG = "<svg viewBox='0 0 24 24' width='38' height='38' fill='none' stroke='currentColor' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'></path></svg>";

  function setView(v) { hub.setAttribute('data-view', v); }
  /* mobile: il focus segue la vista (titolo della chat oppure stanza attiva) */
  function focusAfterView(v) {
    if (!mobile.matches || !window.requestAnimationFrame) return;
    requestAnimationFrame(function () {
      let t = null;
      if (v === 'chat') { t = chatRoot ? chatRoot.querySelector('.kc-title') : null; if (t && !t.hasAttribute('tabindex')) t.setAttribute('tabindex', '-1'); }
      else t = listEl.querySelector('[aria-current="true"]') || listEl.querySelector('button');
      if (t) { try { t.focus({ preventScroll: true }); } catch (e) { /* ok */ } }
    });
  }
  /* il login riporta alla stanza aperta */
  function fixLogin(id) {
    if (!chatRoot) return;
    const path = window.location.pathname + (id === home ? '' : '?room=' + encodeURIComponent(id));
    chatRoot.querySelectorAll('a.kc-guest').forEach(function (a) {
      const base = (a.getAttribute('href') || '').split('return_url=')[0];
      if (base) a.setAttribute('href', base + 'return_url=' + encodeURIComponent(path));
    });
  }
  /* mobile: con la tastiera aperta il pannello segue l'altezza visibile */
  (function () {
    const vv = window.visualViewport;
    if (!vv) return;
    function fit() {
      if (!mobile.matches) { hub.style.removeProperty('--kh-vh'); return; }
      hub.style.setProperty('--kh-vh', Math.round(vv.height) + 'px');
      if (document.activeElement && /TEXTAREA|INPUT/.test(document.activeElement.tagName) && vv.height < window.innerHeight - 120) hub.scrollIntoView({ block: 'start' });
    }
    vv.addEventListener('resize', fit);
    fit();
  })();

  function whenKc() {
    return new Promise(function (resolve) {
      if (chatRoot && chatRoot.__kc) return resolve(chatRoot.__kc);
      chatRoot.addEventListener('kc:ready', function () { resolve(chatRoot.__kc); }, { once: true });
    });
  }

  function roomById(id) {
    for (let i = 0; i < rooms.length; i++) if (rooms[i].id === id) return rooms[i];
    for (let j = 0; j < hits.length; j++) if (hits[j].id === id) return hits[j];
    return null;
  }

  /* universi senza ancora messaggi: si trovano cercandoli per nome */
  async function runSearch(term) {
    const tok = ++searchTok;
    if (term.length < 2) { hits = []; renderList(); return; }
    let found = [];
    try {
      const r = await fetch('/search/suggest.json?q=' + encodeURIComponent(term) + '&resources[type]=collection&resources[limit]=6', { headers: { Accept: 'application/json' } });
      const j = r.ok ? await r.json() : null;
      found = j && j.resources && j.resources.results && j.resources.results.collections ? j.resources.results.collections : [];
    } catch (e) { found = []; }
    found = found.filter(function (c) { return c.handle && c.handle !== home && !rooms.some(function (x) { return x.id === c.handle; }); }).slice(0, 5);
    const vetted = await Promise.all(found.map(async function (c) {
      const info = await getInfo(c.handle);
      if (!info.ok) return null;
      if (await isAdult(c.handle)) return null;
      resolved.set(c.handle, info);
      return { id: c.handle, title: String(c.title || titleCase(c.handle)).slice(0, 80), count: 0, t: 0 };
    }));
    if (tok !== searchTok) return;
    hits = vetted.filter(Boolean).filter(function (h) { return !rooms.some(function (x) { return x.id === h.id; }); });
    renderList();
  }

  /* ----- elenco ----- */
  function renderList() {
    const term = norm(searchEl ? searchEl.value : '');
    const parts = [];
    const homeName = String(cfg.homeName || 'Chat generale');
    if (!term || norm(homeName).indexOf(term) !== -1) {
      parts.push(
        "<button type='button' class='kh-room kh-room--home' data-room='" + esc(home) + "'" + (current === home ? " aria-current='true'" : '') + '>' +
        "<span class='kh-thumb kh-thumb--home'>" + (homeIconSrc() ? "<img src='" + esc(homeIconSrc()) + "' alt='' width='44' height='44'>" : "<svg viewBox='0 0 24 24' width='22' height='22' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'></path></svg>") + "</span>" +
        "<span class='kh-room-text'><span class='kh-room-name'>" + esc(homeName) + "</span><span class='kh-room-meta'>" + esc(cfg.homeMeta || '') + '</span></span></button>'
      );
    }
    const visible = rooms.filter(function (r) {
      const info = resolved.get(r.id);
      if (info && info.ok === false) return false;
      return !term || norm(r.title).indexOf(term) !== -1;
    });
    if (term) { hits.forEach(function (h) { if (!visible.some(function (v) { return v.id === h.id; })) visible.push(h); }); }
    if (roomsLoaded && !roomsError) {
      if (visible.length) {
        parts.push("<div class='kh-group'>" + esc(T.topLabel || 'Universi più commentati') + '</div>');
        visible.forEach(function (r, i) {
          const info = resolved.get(r.id);
          const thumb = info && info.img ? "<img src='" + esc(cdn(info.img, 96)) + "' alt='' width='44' height='44' loading='lazy'>" : '<span>' + esc(r.title.charAt(0).toUpperCase()) + '</span>';
          const medal = !term && i < 3 ? "<span class='kh-medal' role='img' aria-label='" + (i + 1) + "° posto'>" + MEDALS[i] + '</span>' : '';
          const meta = r.count > 0 ? r.count + (r.count === 1 ? ' messaggio' : ' messaggi') + (r.t ? ' · ' + ago(r.t) : '') : EMPTY_META;
          parts.push(
            "<button type='button' class='kh-room' data-room='" + esc(r.id) + "'" + (current === r.id ? " aria-current='true'" : '') + '>' +
            "<span class='kh-thumb'>" + thumb + medal + '</span>' +
            "<span class='kh-room-text'><span class='kh-room-name'>" + esc(r.title) + "</span><span class='kh-room-meta" + (r.count > 0 ? '' : ' is-cta') + "'>" + esc(meta) + '</span></span></button>'
          );
        });
      } else if (!term) {
        parts.push("<p class='kh-empty'>" + esc(T.roomsEmpty || '') + '</p>');
      } else {
        parts.push("<p class='kh-empty'>Nessun universo trovato.</p>");
      }
    } else if (!roomsLoaded) {
      parts.push("<div class='kh-skel'></div><div class='kh-skel'></div><div class='kh-skel'></div>");
    }
    listEl.innerHTML = parts.join('');
  }

  /* carica foto/prodotti delle prime stanze: quelle senza prodotti spariscono dall'elenco */
  async function prepareInfo(list) {
    await Promise.all(list.slice(0, INFO_BATCH).map(function (r) {
      return getInfo(r.id).then(function (v) {
        resolved.set(r.id, v);
      });
    }));
  }

  async function loadRooms() {
    let db;
    for (let i = 0; i < 50 && !window.kojiDb; i++) await new Promise(function (r) { setTimeout(r, 150); });
    db = window.kojiDb;
    if (!db) { roomsLoaded = true; roomsError = true; renderList(); return; }
    let fs;
    try { fs = await import(FIRESTORE_URL); } catch (e) { roomsLoaded = true; roomsError = true; renderList(); return; }
    const q = fs.query(fs.collection(db, 'rooms'), fs.orderBy('count', 'desc'), fs.limit(MAX_ROOMS));
    fs.onSnapshot(q, function (snap) {
      const next = [];
      snap.forEach(function (d) {
        const r = d.data();
        const id = String(r.handle || d.id);
        if (id === home || r.adult) return;
        const c = typeof r.count === 'number' ? r.count : 0;
        if (c <= 0) return;
        next.push({ id: id, title: String(r.title || '').slice(0, 80) || titleCase(id), count: c, t: r.lastMessageAt && typeof r.lastMessageAt.toMillis === 'function' ? r.lastMessageAt.toMillis() : 0 });
      });
      rooms = next;
      prepareInfo(rooms).then(function () {
        roomsLoaded = true;
        roomsError = false;
        if (!introDone) {
          /* entrata a cascata delle stanze, solo la prima volta */
          introDone = true;
          listEl.classList.add('kh-intro');
          setTimeout(function () { listEl.classList.remove('kh-intro'); }, 1500);
        }
        renderList();
        if (current === home) renderSide(home);
      });
    }, function (err) {
      console.error('[koji-chat-hub] elenco stanze non disponibile', err);
      roomsLoaded = true;
      roomsError = true;
      renderList();
    });
  }

  /* ----- pannello laterale ----- */
  function renderSide(id) {
    if (!sideEl) return;
    if (id === home) {
      const bg = asset(cfg.homeSideBg, 900);
      const icon = asset(cfg.homeIcon, 160);
      const tips = (cfg.tips || []).filter(Boolean).map(function (t, i) { return "<li><span class='kh-tip-n'>" + (i + 1) + '</span><span>' + esc(t) + '</span></li>'; }).join('');
      const top = rooms.filter(function (r) { const v = resolved.get(r.id); return !v || v.ok !== false; }).slice(0, 3).map(function (r, i) {
        const v = resolved.get(r.id);
        return "<button type='button' class='kh-mini' data-room='" + esc(r.id) + "'><span class='kh-mini-rank' role='img' aria-label='" + (i + 1) + "° posto'>" + MEDALS[i] + "</span>" +
          (v && v.img ? "<img src='" + esc(cdn(v.img, 80)) + "' alt='' width='36' height='36' loading='lazy'>" : '') +
          "<span class='kh-mini-text'><span class='kh-mini-name'>" + esc(r.title) + "</span><span class='kh-mini-meta'>" + r.count + (r.count === 1 ? ' messaggio' : ' messaggi') + '</span></span></button>';
      }).join('');
      const rules = (cfg.rules || []).filter(Boolean).map(function (t) { return '<li>' + esc(String(t).replace(/^\d+\.\s*/, '').replace(/:\s*$/, '')) + '</li>'; }).join('');
      sideEl.innerHTML =
        "<div class='kh-side-body'><div class='kh-side-hero kh-side-hero--home" + (bg ? ' has-bg' : '') + "'" + (bg ? " style=\"background-image:url('" + esc(bg) + "')\"" : '') + '>' +
        "<span class='kh-side-icon'>" + (icon ? "<img src='" + esc(icon) + "' alt=''>" : CHAT_SVG) + '</span></div>' +
        "<h3 class='kh-side-title'>" + esc(T.homeSideTitle || '') + '</h3>' +
        "<p class='kh-side-text'>" + esc(T.homeSideText || '') + '</p>' +
        (tips ? "<h4 class='kh-side-sub'>" + esc(T.tipsTitle || '') + "</h4><ul class='kh-tips'>" + tips + '</ul>' : '') +
        (top ? "<h4 class='kh-side-sub'>" + esc(T.trendingTitle || '') + "</h4><div class='kh-minis'>" + top + '</div>' : '') +
        (rules ? "<h4 class='kh-side-sub'>" + esc(T.rulesTitle || '') + "</h4><ul class='kh-rulelist'>" + rules + '</ul>' : '') +
        "<button type='button' class='kh-side-btn kh-side-btn--ghost' data-kh-action='rules'>" + esc(T.rulesBtn || 'Regole') + '</button></div>';
      return;
    }
    const r = roomById(id);
    const title = r ? r.title : titleCase(id);
    sideEl.innerHTML = "<div class='kh-side-body'><div class='kh-skel kh-skel--tall'></div></div>";
    getInfo(id).then(function (info) {
      if (current !== id) return;
      const hero = info.ok && info.img ? "<div class='kh-side-hero'><img src='" + esc(cdn(info.img, 480)) + "' alt='' loading='lazy'></div>" : "<div class='kh-side-hero kh-side-hero--home'><span>" + esc(title.charAt(0).toUpperCase()) + '</span></div>';
      let prods = '';
      if (info.ok) {
        prods = "<h4 class='kh-side-sub'>" + esc(T.productsTitle || '') + "</h4><div class='kh-prods'>" + info.products.map(function (p) {
          return "<a class='kh-prod' href='/products/" + esc(encodeURIComponent(p.handle)) + "'>" +
            (p.img ? "<img src='" + esc(cdn(p.img, 240)) + "' alt='' width='120' height='150' loading='lazy'>" : '') +
            "<span class='kh-prod-name'>" + esc(p.title) + "</span><span class='kh-prod-price'>" + esc(money(p.price)) + '</span></a>';
        }).join('') + '</div>';
      }
      sideEl.innerHTML = "<div class='kh-side-body'>" + hero +
        "<h3 class='kh-side-title'>" + esc(title) + '</h3>' +
        (r ? "<p class='kh-side-text'>" + esc(r.count > 0 ? r.count + (r.count === 1 ? ' messaggio' : ' messaggi') + (r.t ? ' · ultimo ' + ago(r.t) : '') : EMPTY_META) + '</p>' : '') +
        "<a class='kh-side-btn' href='/collections/" + esc(encodeURIComponent(id)) + "'>" + esc(T.collectionBtn || "Vai all'universo") + '</a>' + prods +
        "<button type='button' class='kh-side-btn kh-side-btn--ghost' data-kh-action='rules'>" + esc(T.rulesBtn || 'Regole') + '</button></div>';
    });
  }

  /* ----- sfondo sfumato in cima alla chat (mobile): immagine della collezione o del primo prodotto ----- */
  function setCover(id) {
    if (!chatRoot) return;
    const apply = function (u) {
      if (u) chatRoot.style.setProperty('--kc-cover', "url('" + String(u).replace(/'/g, '%27') + "')");
      else chatRoot.style.removeProperty('--kc-cover');
      chatRoot.classList.toggle('has-cover', !!u);
    };
    if (id === home) { apply(asset(cfg.homeSideBg, 900)); return; }
    getInfo(id).then(function (info) {
      if (current !== id) return;
      apply(info && info.ok && info.img ? cdn(info.img, 640) : '');
    });
  }

  /* ----- selezione stanza ----- */
  function select(id, opts) {
    const o = opts || {};
    if (!kc) return;
    current = id;
    const r = roomById(id);
    const title = id === home ? cfg.homeTitle : (r ? r.title : titleCase(id));
    kc.switchRoom(id, { title: title, subtitle: id === home ? cfg.homeSubtitle : cfg.roomSubtitle, adult: false, noMeta: id === home });
    renderList();
    renderSide(id);
    setCover(id);
    setView('chat');
    focusAfterView('chat');
    fixLogin(id);
    if (chatRoot) {
      chatRoot.querySelectorAll('.kc-head, .kc-main').forEach(function (n) {
        n.classList.remove('kh-swap');
        void n.offsetWidth;
        n.classList.add('kh-swap');
      });
    }
    const act = listEl.querySelector('[aria-current="true"]');
    if (act && act.scrollIntoView && !mobile.matches) act.scrollIntoView({ block: 'nearest' });
    if (o.push !== false) {
      try {
        const url = new URL(window.location.href);
        if (id === home) url.searchParams.delete('room'); else url.searchParams.set('room', id);
        history.pushState({ kh: 1 }, '', url.toString());
      } catch (e) { /* ok */ }
    }
  }

  hub.addEventListener('click', function (e) {
    const rb = e.target.closest ? e.target.closest('[data-room]') : null;
    if (rb && hub.contains(rb)) { if (rb.getAttribute('data-room') !== current || mobile.matches) select(rb.getAttribute('data-room')); else setView('chat'); return; }
    const ab = e.target.closest ? e.target.closest('[data-kh-action]') : null;
    if (ab && kc && ab.getAttribute('data-kh-action') === 'rules') kc.openRules();
  });
  if (searchEl) searchEl.addEventListener('input', function () {
    renderList();
    clearTimeout(searchTimer);
    const t = norm(searchEl.value);
    if (!t) { hits = []; searchTok++; renderList(); return; }
    searchTimer = setTimeout(function () { runSearch(t); }, 300);
  });

  window.addEventListener('popstate', function () {
    const p = new URLSearchParams(window.location.search).get('room');
    if (p) { getInfo(p).then(function (v) { return v.ok ? isAdult(p).then(function (a) { if (!a) select(p, { push: false }); }) : null; }); return; }
    if (mobile.matches) { setView('list'); return; }
    select(home, { push: false });
  });

  async function start() {
    kc = await whenKc();
    loadRooms();
    const p = new URLSearchParams(window.location.search).get('room');
    if (p && p !== home) {
      /* si entra solo in stanze di collezioni che esistono davvero (niente nomi inventati nell'indirizzo) */
      const v = await getInfo(p);
      if (v.ok && !(await isAdult(p))) { select(p, { push: false }); return; }
      try { const u = new URL(window.location.href); u.searchParams.delete('room'); history.replaceState(history.state, '', u.toString()); } catch (e) { /* ok */ }
    }
    if (p === home || !mobile.matches) select(home, { push: false });
    else renderList();
  }
  renderList();
  start();
}

function boot() {
  document.querySelectorAll('[data-kh]').forEach(function (hub) {
    if (hub.getAttribute('data-kh-ready') || !hub.hasAttribute('data-view')) return;
    hub.setAttribute('data-kh-ready', '1');
    try { init(hub); } catch (e) { console.error('[koji-chat-hub]', e); }
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', boot);
